import { useEffect, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';

import styles from './styles.module.scss';

const REVIEW_CATEGORIES = [
  {
    key: 'ux_critique',
    title: 'UX Critique',
    description: 'Streamline UX and find usability issues',
    badge: '🧠',
    disabled: false,
  },
  {
    key: 'onboarding_flow',
    title: 'User Onboarding',
    description: 'Uncover UX insights to boost activations',
    badge: '⛳',
    disabled: false,
  },
];

const ONBOARDING_CHIPS = [
  {
    label: 'Simplify Flow',
    text: 'Evaluate the signup/onboarding flow for unnecessary steps and cognitive burden.'
    + ' Identify which steps can be removed, combined, or deferred to create a faster path to value.',
  },
  {
    label: 'First-time UX',
    text: 'Review the onboarding experience for new users. Identify where first-time users struggle to understand value or get started, and suggest clearer introduction flows.',
  },
  {
    label: 'Uncover Features',
    text: 'Analyze feature discoverability issues. Identify why users are missing key features and suggest ways to improve feature visibility and onboarding.',
  },
  {
    label: 'Build Trust',
    text: 'Evaluate elements that impact user trust and confidence. Identify areas where the interface feels unreliable or unprofessional and suggest trust-building improvements.',
  },
];

const UX_CRITIQUE_CHIPS = [
  {
    label: 'Clarify Actions',
    text: 'Review this interface for clarity issues. Identify where users might be confused about functionality, next steps, or purpose. Suggest ways to make actions more obvious.',
  },
  {
    label: 'Fix Drop-Offs',
    text: 'Analyze this user flow for friction points, abandonment triggers, and logical sequence issues. Identify where users get confused or drop off and suggest improvements.',
  },
  {
    label: 'Improve Messaging',
    text: 'Evaluate how the value proposition and pricing are presented. Identify confusion points and suggest clearer ways to communicate benefits and cost structure.',
  },
  {
    label: 'Tidy Layout',
    text: 'Assess content structure and visual hierarchy. Identify areas where information feels overwhelming or'
      + ' poorly organized and suggest better grouping/prioritization.',
  },
];

const CHIP_OPTIONS_BY_CATEGORY = {
  ux_critique: UX_CRITIQUE_CHIPS,
  onboarding_flow: ONBOARDING_CHIPS,
};

const MAX_INPUT_LENGTH = 500;

const sanitizeInputText = text => (text || '').slice(0, MAX_INPUT_LENGTH);

const ProductForm = ({
  productDescription,
  setProductDescription,
  title,
  setTitle,
  audience,
  setAudience,
  businessGoal,
  setBusinessGoal,
  mode = 'form',
  reviewData,
  selectedCategory,
  selectedProblemChips,
  setSelectedProblemChips,
}) => {
  const isDisplayMode = useMemo(() => mode === 'display', [mode]);
  const hasExistingReview = useMemo(() => (
    reviewData
    && typeof reviewData === 'object'
    && Object.keys(reviewData).length > 0
  ), [reviewData]);
  const shouldShowFullForm = !hasExistingReview;

  const displayTitle = useMemo(
    () => reviewData?.title || title || 'No title provided',
    [reviewData?.title, title]
  );

  const displayProduct = useMemo(
    () => reviewData?.extra_data?.user_problem || productDescription || 'No product description provided',
    [reviewData?.extra_data, productDescription]
  );

  const displayAudience = useMemo(
    () => reviewData?.extra_data?.audience || audience || 'No audience provided',
    [reviewData?.extra_data, audience]
  );

  const displayBusinessGoal = useMemo(
    () => reviewData?.extra_data?.business_goal || businessGoal || 'No business goal provided',
    [reviewData?.extra_data, businessGoal]
  );

  const displayCategory = useMemo(() => {
    const auditCategory = reviewData?.extra_data?.template || reviewData?.extra_data?.template;

    return auditCategory || selectedCategory;
  }, [reviewData?.extra_data, selectedCategory]);

  const activeCategory = displayCategory || REVIEW_CATEGORIES[0].key;

  const chipOptions = useMemo(
    () => CHIP_OPTIONS_BY_CATEGORY[activeCategory] || UX_CRITIQUE_CHIPS,
    [activeCategory]
  );

  const activeProblemChip = useMemo(() => {
    if (Array.isArray(selectedProblemChips) && selectedProblemChips.length > 0) {
      return selectedProblemChips[0];
    }

    const auditTags = reviewData?.extra_data?.problem_tags || reviewData?.extra_data?.problemTags;

    if (Array.isArray(auditTags) && auditTags.length > 0) {
      const match = chipOptions.find(option => option.label === auditTags[0] || option.text === auditTags[0]);

      return match ? match.label : auditTags[0];
    }

    return null;
  }, [chipOptions, reviewData?.extra_data, selectedProblemChips]);

  const handleChipToggle = option => {
    if (isDisplayMode) {
      return;
    }

    if (activeProblemChip === option.label) {
      setSelectedProblemChips([]);
      setProductDescription('');

      return;
    }

    setSelectedProblemChips([option.label]);
    setProductDescription(sanitizeInputText(option.text));
  };

  const handleProblemChange = event => {
    if (isDisplayMode) {
      return;
    }

    const sanitizedValue = sanitizeInputText(event.target.value);

    setProductDescription(sanitizedValue);

    if (Array.isArray(selectedProblemChips) && selectedProblemChips.length > 0) {
      setSelectedProblemChips([]);
    }
  };

  const handleAudienceChange = event => {
    if (isDisplayMode) {
      return;
    }

    const sanitizedValue = sanitizeInputText(event.target.value);

    setAudience(sanitizedValue);
  };

  const handleBusinessGoalChange = event => {
    if (isDisplayMode) {
      return;
    }

    const sanitizedValue = sanitizeInputText(event.target.value);

    setBusinessGoal(sanitizedValue);
  };

  useEffect(() => {
    if (isDisplayMode) {
      return;
    }

    if (typeof productDescription === 'string') {
      const sanitized = sanitizeInputText(productDescription);

      if (sanitized !== productDescription) {
        setProductDescription(sanitized);
      }
    }

    if (typeof audience === 'string') {
      const sanitized = sanitizeInputText(audience);

      if (sanitized !== audience) {
        setAudience(sanitized);
      }
    }

    if (typeof businessGoal === 'string') {
      const sanitized = sanitizeInputText(businessGoal);

      if (sanitized !== businessGoal) {
        setBusinessGoal(sanitized);
      }
    }
  }, [isDisplayMode, productDescription, setProductDescription, audience, setAudience, businessGoal, setBusinessGoal]);

  const displayedProblemValue = isDisplayMode
    ? (displayProduct || '')
    : sanitizeInputText(productDescription);

  const displayedAudienceValue = isDisplayMode
    ? (displayAudience || '')
    : sanitizeInputText(audience);

  const displayedBusinessGoalValue = isDisplayMode
    ? (displayBusinessGoal || '')
    : sanitizeInputText(businessGoal);

  // Refs for auto-growing textareas
  const audienceTextareaRef = useRef(null);
  const problemTextareaRef = useRef(null);
  const businessGoalTextareaRef = useRef(null);

  // Expand textarea to fit all content for display mode
  const expandTextareaToContent = textarea => {
    if (!textarea || !isDisplayMode) {
      return;
    }

    textarea.style.height = 'auto';
    textarea.style.height = `${textarea.scrollHeight}px`;
  };

  // Expand textareas in display mode when content loads
  useEffect(() => {
    if (isDisplayMode) {
      // Small delay to ensure DOM is updated with content
      const timer = setTimeout(() => {
        if (audienceTextareaRef.current) {
          expandTextareaToContent(audienceTextareaRef.current);
        }
        if (problemTextareaRef.current) {
          expandTextareaToContent(problemTextareaRef.current);
        }
        if (businessGoalTextareaRef.current) {
          expandTextareaToContent(businessGoalTextareaRef.current);
        }
      }, 0);

      return () => clearTimeout(timer);
    }
  }, [isDisplayMode, displayedAudienceValue, displayedProblemValue, displayedBusinessGoalValue]);

  return (
    <div
      className={cn('flex flex-col gap-10 2xl:gap-12 overflow-auto pb-0', {
        'p-0': isDisplayMode,
        'p-7': !isDisplayMode,
      })}
    >
      {shouldShowFullForm && !isDisplayMode && (
        <div className={styles['context-heading']}>
          <h2 className={styles['context-heading__title']}>
            Add your context for better results
          </h2>
          <span className="text-gray-400 ml-auto">
            *
            {' '}
            Required
          </span>
        </div>
      )}

      <div
        className={cn(
          'flex flex-col gap-10 2xl:gap-12 pr-8 pl-1',
          {
            'overflow-y-auto': isDisplayMode,
          }
        )}
      >
        {shouldShowFullForm && (
          <>
            <label className="text-base text-gray-900 h-5">
              Title
              {' '}
              <span className="text-gray-400">
                *
              </span>
            </label>
            <input
              value={isDisplayMode ? displayTitle : title}
              onChange={e => setTitle(e.target.value)}
              placeholder={'Name of review'}
              readOnly={isDisplayMode}
              className={cn(
                'w-full rounded-lg text-sm resize-none focus:outline-none p-3',
                '2xl:mb-3 mb-5',
                {
                  'bg-gray-50 text-gray-500 border-0': isDisplayMode,
                  'border border-gray-300 text-gray-600 placeholder-gray-500': !isDisplayMode,
                  'focus:ring-2 focus:ring-primary-400 focus:border-transparent': !isDisplayMode,
                }
              )}
            />
          </>
        )}

        {(shouldShowFullForm || displayedAudienceValue) && (
          <>
            <label className="text-base font-medium text-gray-900 h-5">
              Audience
            </label>
            <textarea
              ref={audienceTextareaRef}
              value={isDisplayMode ? displayedAudienceValue : audience}
              onChange={handleAudienceChange}
              placeholder="Who needs this feature"
              readOnly={isDisplayMode}
              maxLength={MAX_INPUT_LENGTH}
              className={cn(
                'w-full rounded-lg text-sm resize-none focus:outline-none p-3',
                {
                  'bg-gray-50 text-gray-500 border-0 mb-5 min-h-auto': isDisplayMode,
                  'border border-gray-300 text-gray-600 placeholder-gray-500 min-h-24 overflow-y-auto': !isDisplayMode,
                  'focus:ring-2 focus:ring-primary-400 focus:border-transparent': !isDisplayMode,
                  'max-h-28': !isDisplayMode,
                }
              )}
            />
            {!isDisplayMode && (
              <div className={cn(styles['problem-meta'], ' mb-5')}>
                <span
                  className={styles['char-counter']}
                  style={{
                    color: MAX_INPUT_LENGTH - displayedAudienceValue.length === 0 ? '#ef4444' : '#94a3b8',
                  }}
                >
                  {MAX_INPUT_LENGTH - displayedAudienceValue.length}
                </span>
              </div>
            )}
          </>
        )}

        <label className="text-base text-gray-900 h-5">
          Problem to solve
        </label>
        <textarea
          ref={problemTextareaRef}
          value={displayedProblemValue}
          onChange={handleProblemChange}
          placeholder="Describe what you want to improve or where users seem to get stuck..."
          readOnly={isDisplayMode}
          maxLength={MAX_INPUT_LENGTH}
          className={cn(
            'w-full rounded-lg text-sm resize-none focus:outline-none p-3',
            {
              'bg-gray-50 text-gray-500 border-0 mb-5 min-h-auto overflow-hidden': isDisplayMode,
              'border border-gray-300 text-gray-600 placeholder-gray-500 min-h-24 overflow-y-auto': !isDisplayMode,
              'focus:ring-2 focus:ring-primary-400 focus:border-transparent': !isDisplayMode,
              'max-h-28 h-1/2 2xl:h-16': !isDisplayMode,
            }
          )}
        />

        {shouldShowFullForm && (
          <div className="flex space-between gap-8">
            {!isDisplayMode && (
              <div className={cn('mb-5', styles['problem-meta'])}>
                <span
                  className={styles['char-counter']}
                  style={{
                    color: MAX_INPUT_LENGTH - displayedProblemValue.length === 0 ? '#ef4444' : '#94a3b8',
                  }}
                >
                  {MAX_INPUT_LENGTH - displayedProblemValue.length}
                </span>
              </div>
            )}
            <div className={styles['problem-chip-list']}>
              {chipOptions.map(option => {
                const isSelected = activeProblemChip === option.label;

                return (
                  <button
                    key={option.label}
                    type="button"
                    onClick={() => handleChipToggle(option)}
                    className={cn(
                      styles['problem-chip'],
                      {
                        [styles['problem-chip--selected']]: isSelected,
                        [styles['problem-chip--disabled']]: isDisplayMode,
                      }
                    )}
                    disabled={isDisplayMode}
                  >
                    {option.label}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {(shouldShowFullForm || displayedBusinessGoalValue) && (
          <>
            <label className="text-base text-gray-900 h-5">
              Business goal
            </label>
            <textarea
              ref={businessGoalTextareaRef}
              value={isDisplayMode ? displayedBusinessGoalValue : businessGoal}
              onChange={handleBusinessGoalChange}
              placeholder="What success looks like"
              readOnly={isDisplayMode}
              maxLength={MAX_INPUT_LENGTH}
              className={cn(
                'w-full rounded-lg text-sm resize-none focus:outline-none p-3',
                {
                  'bg-gray-50 text-gray-500 border-0 mb-5 min-h-auto overflow-hidden': isDisplayMode,
                  'border border-gray-300 text-gray-600 placeholder-gray-500 min-h-24 overflow-y-auto': !isDisplayMode,
                  'focus:ring-2 focus:ring-primary-400 focus:border-transparent': !isDisplayMode,
                  'max-h-28': !isDisplayMode,
                }
              )}
            />
            {!isDisplayMode && (
              <div className={cn('mb-5', styles['problem-meta'])}>
                <span
                  className={styles['char-counter']}
                  style={{
                    color: MAX_INPUT_LENGTH - displayedBusinessGoalValue.length === 0 ? '#ef4444' : '#94a3b8',
                  }}
                >
                  {MAX_INPUT_LENGTH - displayedBusinessGoalValue.length}
                </span>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

ProductForm.propTypes = {
  productDescription: PropTypes.string,
  setProductDescription: PropTypes.func,
  title: PropTypes.string,
  setTitle: PropTypes.func,
  audience: PropTypes.string,
  setAudience: PropTypes.func,
  businessGoal: PropTypes.string,
  setBusinessGoal: PropTypes.func,
  mode: PropTypes.oneOf(['form', 'display']),
  reviewData: PropTypes.shape({
    title: PropTypes.string,
    extra_data: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  }),
  selectedCategory: PropTypes.string,
  selectedProblemChips: PropTypes.arrayOf(PropTypes.string),
  setSelectedProblemChips: PropTypes.func,
};

ProductForm.defaultProps = {
  productDescription: '',
  setProductDescription: () => {},
  title: '',
  setTitle: () => {},
  audience: '',
  setAudience: () => {},
  businessGoal: '',
  setBusinessGoal: () => {},
  mode: 'form',
  reviewData: {},
  selectedCategory: REVIEW_CATEGORIES[0].key,
  selectedProblemChips: [],
  setSelectedProblemChips: () => {},
};

export default ProductForm;
